import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * useFranchiseTelnyx - Reusable hook for Telnyx calling functionality
 * 
 * Provides:
 * - Telnyx status checking
 * - Click-to-call functionality (server-side and native fallback)
 * - Active call popup state management
 * - Call form state management
 * - Call logging to the franchise communications endpoint
 * 
 * @param {Object} options - Configuration options
 * @param {string} options.leadId - The franchise lead ID for logging calls
 * @param {Object} options.lead - The lead object (for name in follow-up tasks)
 * @param {Function} options.onCallLogged - Callback after successful call log
 * @returns {Object} - Hook state and handlers
 */
export function useFranchiseTelnyx({ leadId, lead, onCallLogged } = {}) {
  // Telnyx configuration status
  const [telnyxStatus, setTelnyxStatus] = useState({ 
    configured: false, 
    webrtc_enabled: false 
  });

  // Call state
  const [callingNumber, setCallingNumber] = useState(null);
  const [activeCallPopupOpen, setActiveCallPopupOpen] = useState(false);
  const [activeCallStatus, setActiveCallStatus] = useState('calling'); // 'calling', 'connected', 'ended'
  const [activeCallNumber, setActiveCallNumber] = useState(null);
  const [activeCallLead, setActiveCallLead] = useState(null);

  // WebRTC state (for browser-based calling)
  const [webrtcCallOpen, setWebrtcCallOpen] = useState(false);
  const [webrtcCallNumber, setWebrtcCallNumber] = useState(null);

  // Call dialog state
  const [callDialogOpen, setCallDialogOpen] = useState(false);
  const [savingCall, setSavingCall] = useState(false);

  // Call form state
  const [callForm, setCallForm] = useState({
    phone_number: '',
    call_outcome: 'connected',
    outcome: 'connected', // alias for compatibility
    duration_seconds: 0,
    duration_minutes: 0,
    notes: '',
    recording_id: null,
    recording_url: null,
    schedule_followup: false,
    followup_date: '',
    followup_time: '09:00',
    mark_dnc: false,
    send_email_after: false,
    email_template_id: ''
  });

  // Fetch Telnyx status on mount
  useEffect(() => {
    const fetchTelnyxStatus = async () => {
      try {
        const res = await axios.get(`${API}/telnyx/status`);
        setTelnyxStatus(res.data);
      } catch {
        console.log('Telnyx not configured');
        setTelnyxStatus({ configured: false, webrtc_enabled: false });
      }
    };
    fetchTelnyxStatus();
  }, []);

  /**
   * Initiate a click-to-call
   * @param {string} phoneNumber - The phone number to call
   * @param {Object} targetLead - Optional lead object (for grid-based calling)
   */
  const handleClickToCall = useCallback(async (phoneNumber, targetLead = null) => {
    if (!phoneNumber) return;

    const currentLeadId = targetLead?.id || leadId;
    const currentLead = targetLead || lead;

    // If Telnyx not configured, use native phone dialer
    if (!telnyxStatus.configured) {
      window.location.href = `tel:${phoneNumber}`;
      toast.info('Opening phone dialer...');
      return;
    }

    // Set up call state
    setCallingNumber(phoneNumber);
    setActiveCallNumber(phoneNumber);
    setActiveCallLead(currentLead);
    setActiveCallStatus('calling');
    setActiveCallPopupOpen(true);

    // Pre-fill call form
    setCallForm(prev => ({
      ...prev,
      phone_number: phoneNumber,
      call_outcome: 'connected',
      outcome: 'connected',
      duration_seconds: 0,
      duration_minutes: 0,
      notes: ''
    }));

    // Try server-side call
    try {
      await axios.post(`${API}/telnyx/call`, {
        to_number: phoneNumber,
        lead_id: currentLeadId
      });
      toast.success(`Calling ${phoneNumber}...`);
      
      // Mark as connected after delay
      setTimeout(() => {
        setActiveCallStatus('connected');
      }, 3000);
    } catch (error) {
      // Fall back to tel: link for any Telnyx error
      window.location.href = `tel:${phoneNumber}`;
      toast.info('Opening phone dialer...');
      setActiveCallPopupOpen(false);
    } finally {
      setTimeout(() => setCallingNumber(null), 3000);
    }
  }, [telnyxStatus.configured, leadId, lead]);

  /**
   * Disconnect/end the active call
   * @param {number} elapsedSeconds - Duration of the call in seconds
   */
  const handleDisconnectCall = useCallback(async (elapsedSeconds) => {
    try {
      // Try to hang up via Telnyx (if supported)
      await axios.post(`${API}/telnyx/hangup`, { phone_number: activeCallNumber }).catch(() => {});
    } catch {
      // Ignore errors - just close the popup
    }
    
    setActiveCallStatus('ended');
    toast.info('Call ended');

    // Pre-fill the call form with duration for logging
    setCallForm(prev => ({
      ...prev,
      phone_number: activeCallNumber,
      duration_seconds: elapsedSeconds || 0,
      duration_minutes: Math.ceil((elapsedSeconds || 0) / 60)
    }));
  }, [activeCallNumber]);

  /**
   * Open log call dialog from the active call popup
   * @param {number} elapsedSeconds - Duration of the call in seconds
   */
  const handleLogCallFromPopup = useCallback((elapsedSeconds) => {
    // Pre-fill form with call details
    setCallForm(prev => ({
      ...prev,
      phone_number: activeCallNumber || prev.phone_number,
      duration_seconds: elapsedSeconds || 0,
      duration_minutes: Math.ceil((elapsedSeconds || 0) / 60),
      call_outcome: 'connected',
      outcome: 'connected'
    }));

    // Close the popup and open log call dialog
    setActiveCallPopupOpen(false);
    setActiveCallNumber(null);
    setActiveCallStatus('calling');
    setCallDialogOpen(true);
  }, [activeCallNumber]);

  /**
   * Submit the call log
   */
  const handleLogCallSubmit = useCallback(async () => {
    const currentLeadId = activeCallLead?.id || leadId;
    const currentLead = activeCallLead || lead;

    if (!callForm.phone_number) {
      toast.error('Phone number is required');
      return;
    }

    setSavingCall(true);
    try {
      // Log the call using the shared communications endpoint
      await axios.post(`${API}/communications/call`, {
        franchise_lead_id: currentLeadId,
        phone_number: callForm.phone_number,
        outcome: callForm.outcome || callForm.call_outcome,
        duration_minutes: callForm.duration_minutes || Math.ceil(callForm.duration_seconds / 60),
        notes: callForm.notes,
        recording_url: callForm.recording_url || null
      });

      // Mark DNC if checked
      if (callForm.mark_dnc && currentLeadId) {
        await axios.put(`${API}/franchise/leads/${currentLeadId}`, { dnc: true });
      }

      // Create follow-up task if checked
      if (callForm.schedule_followup && callForm.followup_date && currentLeadId) {
        const leadName = currentLead 
          ? `${currentLead.first_name || ''} ${currentLead.last_name || ''}`.trim() 
          : 'Lead';
        
        await axios.post(`${API}/franchise/leads/${currentLeadId}/tasks`, {
          title: `Follow up call with ${leadName}`,
          description: `Follow-up from call on ${new Date().toLocaleDateString()}`,
          due_date: callForm.followup_date,
          priority: 'medium',
          status: 'pending'
        });
      }

      toast.success('Call logged successfully');
      setCallDialogOpen(false);
      setActiveCallLead(null);

      // Reset call form
      setCallForm({
        phone_number: '',
        call_outcome: 'connected',
        outcome: 'connected',
        duration_seconds: 0,
        duration_minutes: 0,
        notes: '',
        recording_id: null,
        recording_url: null,
        schedule_followup: false,
        followup_date: '',
        followup_time: '09:00',
        mark_dnc: false,
        send_email_after: false,
        email_template_id: ''
      });

      // Call the callback if provided
      if (onCallLogged) {
        onCallLogged();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log call');
    } finally {
      setSavingCall(false);
    }
  }, [activeCallLead, leadId, lead, callForm, onCallLogged]);

  /**
   * Close the active call popup without logging
   */
  const closeCallPopup = useCallback(() => {
    setActiveCallPopupOpen(false);
    setActiveCallNumber(null);
    setActiveCallLead(null);
    setActiveCallStatus('calling');
  }, []);

  /**
   * Close the call dialog without saving
   */
  const closeCallDialog = useCallback(() => {
    setCallDialogOpen(false);
    setActiveCallLead(null);
  }, []);

  // WebRTC handlers (for browser-based calling when enabled)
  const handleWebRTCCallConnected = useCallback((dialedNumber) => {
    setCallForm(prev => ({
      ...prev,
      phone_number: dialedNumber || webrtcCallNumber || prev.phone_number,
      notes: '',
      call_outcome: 'connected',
      outcome: 'connected',
      duration_seconds: 0
    }));
  }, [webrtcCallNumber]);

  const handleWebRTCCallEnded = useCallback((duration) => {
    setCallForm(prev => ({ ...prev, duration_seconds: duration }));
    setWebrtcCallOpen(false);
    setWebrtcCallNumber(null);
    setCallDialogOpen(true);
  }, []);

  const handleDurationUpdate = useCallback((duration) => {
    setCallForm(prev => ({ 
      ...prev, 
      duration_seconds: duration,
      duration_minutes: Math.ceil(duration / 60)
    }));
  }, []);

  const handleWebRTCError = useCallback((errorMsg) => {
    toast.error(`WebRTC call failed: ${errorMsg}`);
    setWebrtcCallOpen(false);
    // Fall back to native phone dialer
    if (webrtcCallNumber) {
      window.location.href = `tel:${webrtcCallNumber}`;
      toast.info('Opening phone dialer as fallback...');
    }
    setWebrtcCallNumber(null);
  }, [webrtcCallNumber]);

  return {
    // Telnyx status
    telnyxStatus,

    // Call state
    callingNumber,
    activeCallPopupOpen,
    activeCallStatus,
    activeCallNumber,
    activeCallLead,

    // WebRTC state
    webrtcCallOpen,
    webrtcCallNumber,
    setWebrtcCallOpen,
    setWebrtcCallNumber,

    // Dialog state
    callDialogOpen,
    setCallDialogOpen,
    savingCall,

    // Form state
    callForm,
    setCallForm,

    // Handlers
    handleClickToCall,
    handleDisconnectCall,
    handleLogCallFromPopup,
    handleLogCallSubmit,
    closeCallPopup,
    closeCallDialog,

    // WebRTC handlers
    handleWebRTCCallConnected,
    handleWebRTCCallEnded,
    handleDurationUpdate,
    handleWebRTCError
  };
}

export default useFranchiseTelnyx;
